import React, { useState, useEffect } from "react";
import axios from 'axios';
import { FaEnvelope, FaLock, FaEye, FaEyeSlash, FaSignInAlt } from "react-icons/fa";
import { endpoints } from "../Apis/endpoints";
import { BASE_URL } from "../Apis/endpoints";
import ForgotPassword from "./ForgetPassword";
import { FcGoogle } from "react-icons/fc";

const Login = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [authError, setAuthError] = useState("");
    const [fullname, setFullname] = useState("");
    const [rememberMe, setRememberMe] = useState(false);
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [googleUrl, setGoogleUrl] = useState('');
    const [isHeightMoreThan740, setIsHeightMoreThan740] = useState(false);

    useEffect(() => {
        const checkHeight = () => {
            setIsHeightMoreThan740(window.innerHeight > 740);
        };
        checkHeight();
        window.addEventListener("resize", checkHeight);
        return () => window.removeEventListener("resize", checkHeight);
    }, []);

    const handleGoogleLogin = () => {
        if (googleUrl) {
            window.location.href = googleUrl;
        } else {
            console.error("Google URL not available.");
        }
    };

    const fetchGoogleAuthUrl = async () => {
        try {
            const response = await axios.get(`${BASE_URL}${endpoints.GOOGLE_URL}`);
            setGoogleUrl(response.data.url);
        } catch (error) {
            console.error("Error fetching Google auth URL:", error.response?.data || error.message);
        }
    };

    useEffect(() => {
        fetchGoogleAuthUrl();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        const newErrors = {};

        if (!email) newErrors.email = "Email is required";
        else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = "Email is invalid";

        if (!password) newErrors.password = "Password is required";
        else if (password.length < 6) newErrors.password = "Password must be at least 6 characters";

        setErrors(newErrors);

        if (Object.keys(newErrors).length === 0) {
            try {
                const response = await axios.post(`${BASE_URL}${endpoints.SIGNIN}`, {
                    email,
                    password,
                    remember_me: rememberMe,
                });

                localStorage.setItem('access_token', response.data.access);
                localStorage.setItem('refresh_token', response.data.refresh);
                localStorage.setItem('user', response.data.fullname);
                localStorage.setItem('user_id', response.data.user_id);

                setFullname(response.data.fullname);
                setAuthError("");

                if (response.data.first_login) {
                    window.location.href = "/intro1";
                } else {
                    window.location.href = "/upload";
                }
            } catch (error) {
                setAuthError("Invalid email or password.");
                console.error("Error logging in:", error.response?.data || error.message);
            }
        }
    };

    if (showForgotPassword) {
        return <ForgotPassword />;
    }

    return (
        <div
            className="w-full min-h-screen flex flex-col items-center bg-white px-4 sm:px-8 md:px-10 pt-0"
            style={{
                backgroundImage: "url('/images/background.svg')",
                backgroundSize: "100% auto",
                backgroundAttachment: isHeightMoreThan740 ? "fixed" : "scroll",
                overflowX: "hidden",
            }}
        >
            <div className="flex-1 flex flex-col items-center w-full max-w-md">
                <img
                    src="/images/Transpoze_logo.svg"
                    alt="Transpoze logo"
                    className="mt-16 max-w-xs sm:mt-20 md:mt-24"
                />
                <div className="w-full flex justify-center mt-10">
                    {authError && (
                        <p className="text-red-500 font-sans max-w-2xl text-xl rounded-lg shadow-md p-2">
                            {authError}
                        </p>
                    )}
                    {fullname && <p className="text-green-500 text-sm mt-2">Welcome, {fullname}!</p>}
                </div>
                <h1
                    className="text-3xl sm:text-2xl text-[#1E293B] font-bold font-[sans,Plus Jakarta Sans] mt-4"
                    style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
                >
                    Sign In To Your Account
                </h1>
                <form onSubmit={handleSubmit} className="font-semibold rounded w-full max-w-md space-y-4 mt-9">
                    <div>
                        <label className="block mb-2 text-[#1E293B]">Email Address</label>
                        <div className="flex items-center border border-gray-300 rounded-full bg-white">
                            <FaEnvelope className="ml-4 text-[#475569]" />
                            <input
                                type="email"
                                placeholder="Enter your email address"
                                className="p-3 w-full rounded-full bg-[#fff] text-[#475569] focus:outline-none"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>

                    <div>
                        <label className="block mb-2 text-[#1E293B]">Password</label>
                        <div className="flex items-center border border-gray-300 rounded-full bg-white">
                            <FaLock className="ml-4 text-[#475569]" />
                            <input
                                type={showPassword ? "text" : "password"}
                                placeholder="Enter your password"
                                className="p-3 w-full rounded-full text-[#475569] bg-none focus:outline-none pr-10"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                            <button
                                type="button"
                                className="relative sm:mr-3 md:mr-4 mr-4 text-[#475569] focus:outline-none"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? <FaEye /> : <FaEyeSlash />}
                            </button>
                        </div>
                        {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                    </div>

                    <div className="flex items-center justify-between">
                        <label className="flex items-center text-sm">
                            <input
                                type="checkbox"
                                className="mr-2"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            Remember Me
                        </label>
                        <button
                            type="button"
                            onClick={() => setShowForgotPassword(true)}
                            className="text-[#2C67F2] text-sm hover:underline"
                        >
                            Forgot Password?
                        </button>
                    </div>

                    <button
                        type="submit"
                        className="bg-[#2C67F2] text-white p-3 rounded-full w-full hover:bg-blue-600 flex justify-center items-center"
                    >
                        Sign In
                        <FaSignInAlt className="text-lg ml-2" />
                    </button>
                </form>

                <p className="mt-4 text-[#1E293B] font-semibold">
                    Don't Have an account? <a href="/signup" className="text-blue-500 hover:underline">Sign Up</a>
                </p>

                <div className="flex items-center justify-center my-4 w-full max-w-md">
                    <hr className="flex-grow border-t border-gray-300" />
                    <span className="mx-2 text-gray-500">OR</span>
                    <hr className="flex-grow border-t border-gray-300" />
                </div>

                <button
                    onClick={handleGoogleLogin}
                    className="bg-[#333333] text-white text-sm p-3 font-semibold rounded-full w-full max-w-md flex items-center justify-center hover:bg-black mt-4"
                >
                    <FcGoogle className="w-6 h-6" />
                    <span className="ml-2">Sign in with Google</span>
                </button>
            </div>

            <footer className="mt-auto w-full text-[#475569] font-semibold text-sm flex justify-center items-center border-t border-[#CBD5E1] py-2">
                Copyright {new Date().getFullYear()} Transpoze ©
            </footer>
        </div>
    );
};

export default Login;
